<template>
  <section id="projects" class="py-20 bg-gray-50 dark:bg-gray-900">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="section-title" :class="{ 'khmer': isKhmer }">
        {{ $t('projects.title') }}
      </h2>

      <!-- Filter Tags -->
      <div class="flex flex-wrap justify-center gap-4 mb-12">
        <button
          @click="activeFilter = 'all'"
          :class="[
            'px-6 py-2 rounded-full text-sm font-medium transition-all duration-200',
            activeFilter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
          ]"
        >
          {{ $t('projects.allProjects') }}
        </button>
        <button
          v-for="tag in availableTags"
          :key="tag"
          @click="activeFilter = tag"
          :class="[
            'px-6 py-2 rounded-full text-sm font-medium transition-all duration-200',
            activeFilter === tag
              ? 'bg-primary-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
          ]"
        >
          {{ tag }}
        </button>
      </div>

      <!-- Projects Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div
          v-for="project in filteredProjects"
          :key="project.id"
          class="card p-6 group hover:scale-105 transition-all duration-300"
        >
          <!-- Project Image -->
          <div class="relative mb-6 rounded-lg overflow-hidden bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/20 dark:to-purple-900/20 h-48">
            <div class="absolute inset-0 flex items-center justify-center">
              <span class="text-4xl">{{ project.icon }}</span>
            </div>
            <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>

          <!-- Project Info -->
          <div class="space-y-4">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ project.title }}
            </h3>
            
            <p class="text-gray-600 dark:text-gray-300 text-sm line-clamp-3">
              {{ project.description }}
            </p>

            <!-- Tech Stack Tags -->
            <div class="flex flex-wrap gap-2">
              <span
                v-for="tech in project.technologies"
                :key="tech"
                class="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-full"
              >
                {{ tech }}
              </span>
            </div>

            <!-- Action Buttons -->
            <!-- <div class="flex gap-4 pt-4">
              <a
                :href="project.liveUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 btn-primary text-center text-sm py-2"
              >
                <EyeIcon class="w-4 h-4 inline mr-2" />
                {{ $t('projects.viewDemo') }}
              </a>
              <a
                :href="project.githubUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 btn-secondary text-center text-sm py-2"
              >
                <CodeBracketIcon class="w-4 h-4 inline mr-2" />
                {{ $t('projects.viewCode') }}
              </a>
            </div> -->
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
// import { EyeIcon, CodeBracketIcon } from '@heroicons/vue/24/outline'

const store = useStore()
const isKhmer = computed(() => store.getters.isKhmer)

const activeFilter = ref('all')

const projects = [
  {
    id: 1,
    title: 'E-Commerce Platform',
    description: 'A full-stack e-commerce platform built with Vue.js, Node.js, and MongoDB. Features include user authentication, product catalog, shopping cart, and payment integration.',
    icon: '🛒',
    technologies: ['Vue.js', 'Node.js', 'MongoDB', 'Stripe'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/karonasrun/ecommerce',
    tags: ['Vue.js', 'Node.js', 'Full Stack']
  },
  {
    id: 2,
    title: 'Task Management App',
    description: 'A collaborative task management application with real-time updates, drag-and-drop functionality, and team collaboration features.',
    icon: '📋',
    technologies: ['React', 'Socket.io', 'Express', 'PostgreSQL'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/karonasrun/taskmanager',
    tags: ['React', 'Real-time', 'Collaboration']
  },
  {
    id: 3,
    title: 'Weather Dashboard',
    description: 'A responsive weather dashboard with location-based forecasting, interactive maps, and detailed weather analytics.',
    icon: '🌤️',
    technologies: ['Vue.js', 'OpenWeather API', 'Chart.js'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/karonasrun/weather',
    tags: ['Vue.js', 'API Integration', 'Data Visualization']
  },
  {
    id: 4,
    title: 'Social Media Dashboard',
    description: 'A comprehensive social media management tool for scheduling posts, analytics, and managing multiple accounts.',
    icon: '📱',
    technologies: ['React', 'Node.js', 'Redis', 'AWS'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/karonasrun/social-dashboard',
    tags: ['React', 'Node.js', 'AWS', 'Analytics']
  },
  {
    id: 5,
    title: 'Learning Management System',
    description: 'An educational platform with course management, video streaming, quizzes, and progress tracking for online learning.',
    icon: '🎓',
    technologies: ['Vue.js', 'Laravel', 'MySQL', 'FFmpeg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/karonasrun/lms',
    tags: ['Vue.js', 'Laravel', 'Education', 'Video Streaming']
  },
  {
    id: 6,
    title: 'Portfolio Website',
    description: 'A modern, responsive portfolio website with dark mode, multilingual support, and smooth animations.',
    icon: '💼',
    technologies: ['Vue.js', 'Tailwind CSS', 'Vite'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/karonasrun/portfolio',
    tags: ['Vue.js', 'Tailwind CSS', 'Portfolio']
  }
]

const availableTags = computed(() => {
  const tags = new Set()
  projects.forEach(project => {
    project.tags.forEach(tag => tags.add(tag))
  })
  return Array.from(tags)
})

const filteredProjects = computed(() => {
  return activeFilter.value === 'all' 
    ? projects 
    : projects.filter(project => project.tags.includes(activeFilter.value))
})
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 